import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  useTheme,
  Tooltip,
  Skeleton,
  Drawer,
  Divider,
  TextField,
  FormControl,
  FormLabel,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { XIcon, CopyIcon } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { FlowNodeData } from './CustomNodes';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { getWindmillScriptApi } from '../../utils/apis';
import { RootState } from '../../utils/redux/redux';
import {
  setCurrentFlow,
  updateFormData,
  setIsExecuting,
  setExecutionResult,
  setError,
  validateFormData,
} from '../../utils/redux/slices/windmillFlowSlice';
import { runWindmillWorkflowApi } from '../../utils/apis';
import { commonStyles } from '../../utils/styles/inputs';
import NoDataFallback from '../common/NoDataFallback';

interface NodeDetailsSidebarProps {
  open: boolean;
  onClose: () => void;
  nodeData: FlowNodeData | null;
  nodeId: string | null;
}

const NodeDetailsSidebar: React.FC<NodeDetailsSidebarProps> = ({
  open,
  onClose,
  nodeData,
  nodeId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { copy } = useCopyToClipboard();
  const queryClient = useQueryClient();

  // Redux state for windmill flow form
  const {
    currentFlow,
    formData,
    isExecuting,
    executionResult,
    error,
    validationErrors,
  } = useSelector((state: RootState) => state.windmillFlow);

  // Fetch script details when node type is script and path is available
  const {
    data: scriptData,
    isLoading: scriptLoading,
    error: scriptError,
    isError: isScriptError,
  } = useQuery({
    queryKey: ['windmill-script', nodeData?.path],
    queryFn: () => getWindmillScriptApi(nodeData?.path || ''),
    enabled:
      !!nodeData?.path &&
      (nodeData?.type === 'script' || nodeData?.type === 'rawscript'),
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Set current flow when input node is opened or when flow data changes
  React.useEffect(() => {
    if (nodeData?.type === 'script' && nodeData?.flowData) {
      // Check if the flow data has actually changed
      if (!currentFlow || currentFlow.path !== nodeData.flowData.path) {
        dispatch(setCurrentFlow(nodeData.flowData));
      }
    }
  }, [nodeData, currentFlow, dispatch]);

  // Early return for missing required data
  if (!nodeData || !nodeId) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: '700px',
              p: 2,
              backgroundColor: '#FFFFFF',
            },
          },
        }}
      >
        <NoDataFallback
          message="No Node Data Available"
          description="The selected node data could not be loaded. Please try selecting a different node."
          httpStatusCode={404}
        />
      </Drawer>
    );
  }

  const getNodeTypeColor = () => {
    switch (nodeData.type) {
      case 'script':
      case 'rawscript':
        return theme.palette.primary.main;
      case 'branchone':
        return theme.palette.warning.main;
      case 'trigger':
        return theme.palette.success.main;
      case 'error_handler':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[600];
    }
  };

  const handleCopyContent = () => {
    const content = scriptData?.data?.content || nodeData.content;
    if (content) {
      copy(content);
    }
  };

  const handleCopyPath = () => {
    if (nodeData.path) {
      copy(nodeData.path);
    }
  };

  const handleCopyExpression = () => {
    if (nodeData.expression) {
      copy(nodeData.expression);
    }
  };

  // Get script content from API response or fallback to node data
  const getScriptContent = () => {
    if (scriptData?.data?.content) {
      return scriptData.data.content;
    }
    return nodeData.content;
  };

  // Get script description from API response
  const getScriptDescription = () => {
    return scriptData?.data?.description;
  };

  // Get script language from API response
  const getScriptLanguage = () => {
    return scriptData?.data?.language;
  };

  // Check if this is a script node
  const isScriptNode =
    nodeData.type === 'script' || nodeData.type === 'rawscript';

  // Check if this is an input node (script node with flow data)
  const isInputNode = isScriptNode && nodeData.flowData;

  // Check if form is valid
  const isFormValid = currentFlow?.schema
    ? Object.keys(validationErrors).length === 0
    : true;

  // Form handlers for input node
  const handleInputChange = (field: string, value: any) => {
    dispatch(updateFormData({ field, value }));
  };

  const handleSubmit = async () => {
    if (!currentFlow) return;

    // Validate form before submitting
    if (currentFlow.schema) {
      const { isValid } = validateFormData(formData, currentFlow.schema);
      if (!isValid) {
        dispatch(setError('Please fill in all required fields'));
        return;
      }
    }

    dispatch(setIsExecuting(true));
    dispatch(setError(null));

    try {
      const result = await runWindmillWorkflowApi(currentFlow.path, formData);
      dispatch(setExecutionResult(result));
      // Invalidate all windmillJobs queries to ensure execution history updates
      queryClient.invalidateQueries({
        queryKey: ['windmillJobs'],
        exact: false,
      });
    } catch (err: any) {
      dispatch(
        setError(err?.response?.data?.message || 'Failed to execute workflow')
      );
    } finally {
      dispatch(setIsExecuting(false));
    }
  };

  const renderFormField = (field: string, prop: any) => {
    const value = formData[field] || '';
    const isRequired = currentFlow?.schema?.required?.includes(field);
    const hasError = !!validationErrors[field];

    switch (prop.type) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={e => handleInputChange(field, e.target.checked)}
                color="primary"
              />
            }
            label={prop.description || field}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: 14,
                fontWeight: 500,
                color: theme.palette.text.primary,
              },
            }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            value={value}
            onChange={e => handleInputChange(field, Number(e.target.value))}
            required={isRequired}
            error={hasError}
            helperText={validationErrors[field]}
            sx={commonStyles.textField}
            InputProps={{
              inputProps: {
                min: 0,
                step: 0.1,
              },
            }}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            value={value}
            onChange={e => handleInputChange(field, e.target.value)}
            required={isRequired}
            error={hasError}
            helperText={validationErrors[field]}
            sx={commonStyles.textField}
            multiline={prop.description?.length > 50}
            rows={prop.description?.length > 50 ? 3 : 1}
            InputProps={{
              ...(prop.description?.length > 50 && {
                sx: {
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    minHeight: '40px',
                  },
                },
              }),
            }}
          />
        );
    }
  };

  // Create skeleton component for reusability
  const SkeletonSection = ({
    lines = 3,
    width = ['100%', '90%', '95%'],
  }: {
    lines?: number;
    width?: string[];
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          width={width[index % width.length]}
          height={20}
        />
      ))}
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '700px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {scriptLoading && isScriptNode ? (
                <>
                  <Skeleton variant="text" width={200} height={24} />
                  <Skeleton variant="text" width={150} height={20} />
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: 'custom.textBlue',
                      letterSpacing: '-0.2px',
                    }}
                  >
                    {isInputNode ? 'Execute Workflow' : nodeData.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: 14,
                      color: 'custom.textBlue',
                      letterSpacing: '-0.2px',
                      opacity: 0.5,
                    }}
                  >
                    {isInputNode
                      ? currentFlow?.description
                      : getScriptDescription()}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Language Chip in Header */}
          {scriptLoading && isScriptNode ? (
            <Skeleton
              variant="rectangular"
              width={80}
              height={24}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            (getScriptLanguage() || nodeData.language) && (
              <Chip
                label={getScriptLanguage() || nodeData.language}
                size="small"
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 500,
                }}
              />
            )
          )}
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 2,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* For input nodes, show the form */}
          {isInputNode && currentFlow ? (
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {executionResult && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Workflow executed successfully!
                </Alert>
              )}

              {/* Input Parameters Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: `1px solid ${theme.palette.lightGray.main}`,
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: theme.palette.text.primary,
                    fontSize: 16,
                  }}
                >
                  Input Parameters
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentFlow.schema?.order?.map(field => {
                    const prop = currentFlow.schema.properties[field];
                    if (!prop) return null;

                    return (
                      <FormControl key={field} fullWidth>
                        <FormLabel sx={commonStyles.formLabel}>
                          {prop.description || field}
                          {currentFlow.schema.required?.includes(field) && (
                            <span style={{ color: theme.palette.error.main }}>
                              {' '}
                              *
                            </span>
                          )}
                        </FormLabel>
                        {renderFormField(field, prop)}
                      </FormControl>
                    );
                  })}
                </Box>
              </Paper>

              {/* Execution Result Section */}
              {executionResult && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: theme.palette.text.primary,
                      fontSize: 16,
                    }}
                  >
                    Execution Result
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: 14,
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    <pre>{JSON.stringify(executionResult, null, 2)}</pre>
                  </Box>
                </Paper>
              )}

              {/* Execute Button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={isExecuting || !isFormValid}
                  startIcon={
                    isExecuting ? <CircularProgress size={16} /> : null
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    fontSize: 14,
                    px: 2,
                    py: 1,
                    opacity: isFormValid ? 1 : 0.6,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  {isExecuting ? 'Executing...' : 'Execute Workflow'}
                </Button>
              </Box>

              {/* Show validation status */}
              {!isFormValid && Object.keys(validationErrors).length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please fill in all required fields before executing the
                  workflow
                </Alert>
              )}
            </Box>
          ) : (
            /* For non-input nodes, show the original content */
            <>
              {/* For script nodes, show only content */}
              {isScriptNode ? (
                <>
                  {/* Content/Script */}
                  {getScriptContent() && (
                    <Box
                      sx={{
                        height: '100%',
                        overflow: 'auto',
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 1,
                      }}
                    >
                      {scriptLoading ? (
                        <SkeletonSection
                          lines={7}
                          width={[
                            '100%',
                            '90%',
                            '95%',
                            '85%',
                            '92%',
                            '88%',
                            '94%',
                          ]}
                        />
                      ) : isScriptError ? (
                        <Box sx={{ p: 2 }}>
                          <NoDataFallback
                            message="Failed to Load Script"
                            description={
                              scriptError?.message?.includes('404') ||
                              scriptError?.message?.includes('not found')
                                ? `Script not found at path: ${nodeData.path}. The script may have been moved or deleted.`
                                : scriptError?.message?.includes('403') ||
                                    scriptError?.message?.includes('forbidden')
                                  ? `Access denied to script: ${nodeData.path}. You may not have permission to view this script.`
                                  : `Unable to load script content. Please check your connection and try again.`
                            }
                            httpStatusCode={
                              scriptError?.message?.includes('404')
                                ? 404
                                : scriptError?.message?.includes('403')
                                  ? 403
                                  : 500
                            }
                            clickableWords={{
                              'try again': '#',
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            lineHeight: 1.5,
                            px: 1,
                          }}
                        >
                          {getScriptContent()}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Handle case where script content is empty or failed to load */}
                  {!getScriptContent() && !scriptLoading && !isScriptError && (
                    <Box sx={{ p: 2 }}>
                      <NoDataFallback
                        message="No Script Content"
                        description="This script node does not contain any content or the content failed to load."
                        httpStatusCode={404}
                      />
                    </Box>
                  )}
                </>
              ) : (
                /* For non-script nodes, show all the original details */
                <>
                  {/* Basic Information */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.lightGray.main}`,
                      borderRadius: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: theme.palette.text.primary,
                        fontSize: 16,
                      }}
                    >
                      Basic Information
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      {/* Node ID */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.lightGray.text,
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block',
                            fontSize: 14,
                          }}
                        >
                          Node ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            backgroundColor: theme.palette.grey[50],
                            p: 1,
                            borderRadius: 1,
                            fontSize: 14,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {nodeId}
                        </Typography>
                      </Box>

                      {/* Label */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.lightGray.text,
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block',
                            fontSize: 14,
                          }}
                        >
                          Label
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          {nodeData.label}
                        </Typography>
                      </Box>

                      {/* Type */}
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.lightGray.text,
                            fontWeight: 500,
                            mb: 0.5,
                            display: 'block',
                            fontSize: 14,
                          }}
                        >
                          Type
                        </Typography>
                        <Chip
                          label={nodeData.type}
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.custom.sidebarIconBg,
                            color: getNodeTypeColor(),
                            fontWeight: 500,
                            fontSize: 12,
                          }}
                        />
                      </Box>

                      {/* Tag */}
                      {nodeData.tag && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.lightGray.text,
                              fontWeight: 500,
                              mb: 0.5,
                              display: 'block',
                              fontSize: 14,
                            }}
                          >
                            Tag
                          </Typography>
                          <Chip
                            label={nodeData.tag}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.text.secondary,
                              fontSize: 12,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Paper>

                  {/* Path Information */}
                  {nodeData.path && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.lightGray.main}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: 16,
                          }}
                        >
                          Path
                        </Typography>
                        <Tooltip title="Copy Path">
                          <IconButton
                            onClick={handleCopyPath}
                            size="small"
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': { backgroundColor: '#E8EBF1' },
                            }}
                          >
                            <CopyIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: theme.palette.grey[50],
                          p: 1,
                          borderRadius: 1,
                          fontSize: 14,
                          wordBreak: 'break-all',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {nodeData.path}
                      </Typography>
                    </Paper>
                  )}

                  {/* Expression (for branch nodes) */}
                  {nodeData.expression && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.lightGray.main}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: 16,
                          }}
                        >
                          Expression
                        </Typography>
                        <Tooltip title="Copy Expression">
                          <IconButton
                            onClick={handleCopyExpression}
                            size="small"
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': { backgroundColor: '#E8EBF1' },
                            }}
                          >
                            <CopyIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: theme.palette.grey[50],
                          p: 1,
                          borderRadius: 1,
                          fontSize: 14,
                          wordBreak: 'break-all',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {nodeData.expression}
                      </Typography>
                    </Paper>
                  )}

                  {/* Summary */}
                  {nodeData.summary && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.lightGray.main}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: theme.palette.text.primary,
                          fontSize: 16,
                        }}
                      >
                        Summary
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: 14,
                          fontWeight: 400,
                        }}
                      >
                        {nodeData.summary}
                      </Typography>
                    </Paper>
                  )}

                  {/* Language */}
                  {nodeData.language && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.lightGray.main}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: theme.palette.text.primary,
                          fontSize: 16,
                        }}
                      >
                        Language
                      </Typography>
                      <Chip
                        label={nodeData.language}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                          fontWeight: 500,
                          fontSize: 12,
                        }}
                      />
                    </Paper>
                  )}

                  {/* Content */}
                  {nodeData.content && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: `1px solid ${theme.palette.lightGray.main}`,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            fontSize: 16,
                          }}
                        >
                          Content
                        </Typography>
                        <Tooltip title="Copy Content">
                          <IconButton
                            onClick={handleCopyContent}
                            size="small"
                            sx={{
                              color: theme.palette.text.secondary,
                              '&:hover': { backgroundColor: '#E8EBF1' },
                            }}
                          >
                            <CopyIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          maxHeight: '300px',
                          overflow: 'auto',
                          backgroundColor: theme.palette.grey[50],
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            color: theme.palette.text.primary,
                          }}
                        >
                          {nodeData.content}
                        </Typography>
                      </Box>
                    </Paper>
                  )}

                  {/* Input Transforms */}
                  {nodeData.input_transforms &&
                    Object.keys(nodeData.input_transforms).length > 0 && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: `1px solid ${theme.palette.lightGray.main}`,
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            mb: 1.5,
                            color: theme.palette.text.primary,
                            fontSize: 16,
                          }}
                        >
                          Input Transforms
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {Object.entries(nodeData.input_transforms).map(
                            ([key, value]) => (
                              <Box key={key}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.lightGray.text,
                                    fontWeight: 500,
                                    display: 'block',
                                    fontSize: 14,
                                  }}
                                >
                                  {key}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    backgroundColor: theme.palette.grey[50],
                                    p: 1,
                                    borderRadius: 1,
                                    fontSize: 14,
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {JSON.stringify(value, null, 2)}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                      </Paper>
                    )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default NodeDetailsSidebar;
